import React, { useEffect, useState } from 'react';
import { Check, Palette } from 'lucide-react';
import {
  DEFAULT_THEME_ID,
  getTheme,
  getThemeList,
  isThemeId,
} from '../../config/themes';
import { getPlatformSettings, updatePlatformSettings } from '../../services/firestoreService';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Admin platform theme panel — select a theme, preview locally, then Apply to save for everyone.
 */
const AdminThemesPanel = () => {
  const { setPlatformTheme, setPreviewTheme, clearPreviewTheme, clearUserTheme } = useTheme();
  const [appliedThemeId, setAppliedThemeId] = useState(DEFAULT_THEME_ID);
  const [pendingThemeId, setPendingThemeId] = useState(DEFAULT_THEME_ID);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const themes = getThemeList();
  const hasChanges = pendingThemeId !== appliedThemeId;

  useEffect(() => {
    let mounted = true;
    (async () => {
      const result = await getPlatformSettings();
      if (mounted && result.success && result.settings?.defaultThemeId) {
        const id = result.settings.defaultThemeId;
        if (isThemeId(id)) {
          setAppliedThemeId(id);
          setPendingThemeId(id);
          setPlatformTheme(id);
        }
      }
    })();
    return () => { mounted = false; };
  }, [setPlatformTheme]);

  const handlePick = (themeId) => {
    if (!isThemeId(themeId) || saving) return;
    setPendingThemeId(themeId);
    setPreviewTheme(themeId);
    setMessage({
      type: 'info',
      text: 'Previewing theme on your screen. Click Apply platform theme to save for all users.',
    });
  };

  const handleApply = async () => {
    if (!isThemeId(pendingThemeId) || saving || !hasChanges) return;
    setSaving(true);
    setMessage({ type: '', text: '' });

    const settingsResult = await getPlatformSettings();
    const current = settingsResult.success ? settingsResult.settings || {} : {};
    const result = await updatePlatformSettings({ ...current, defaultThemeId: pendingThemeId });

    if (result.success) {
      setAppliedThemeId(pendingThemeId);
      clearPreviewTheme();
      clearUserTheme();
      setPlatformTheme(pendingThemeId);
      setMessage({
        type: 'success',
        text: `Applied "${getTheme(pendingThemeId).name}" as the platform default. All users without a personal theme will see this after refresh.`,
      });
    } else {
      setPendingThemeId(appliedThemeId);
      setPreviewTheme(appliedThemeId);
      setMessage({ type: 'error', text: `Apply failed: ${result.error || 'Unknown error'}` });
    }
    setSaving(false);
  };

  const handleCancel = () => {
    setPendingThemeId(appliedThemeId);
    clearPreviewTheme();
    setMessage({ type: '', text: '' });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Palette className="w-7 h-7 text-[#0c0f14]" />
          Themes
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Choose a theme, preview it, then click Apply. This sets the default for admin and all user roles.
        </p>
      </div>

      {message.text && (
        <div
          className={`rounded-xl border px-4 py-3 text-sm ${
            message.type === 'error'
              ? 'border-red-200 bg-red-50 text-red-800'
              : message.type === 'success'
              ? 'border-[#dcebe4] bg-[#eaf3ef] text-[#0c0f14]'
              : 'border-[#e8ece9] bg-[#f6f7f5] text-[#5c6670]'
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="card">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Platform default theme</h3>
            <p className="text-xs text-[#5c6670] mt-1">
              Active: <strong>{getTheme(appliedThemeId).name}</strong>
              {hasChanges && (
                <span> · Previewing: <strong>{getTheme(pendingThemeId).name}</strong></span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {hasChanges && (
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className="px-4 py-2 rounded-xl border border-[#e8ece9] text-sm font-medium text-[#5c6670] hover:bg-[#f6f7f5] disabled:opacity-50"
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              onClick={handleApply}
              disabled={saving || !hasChanges}
              className="px-5 py-2 rounded-xl bg-[#0c0f14] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? 'Applying…' : 'Apply platform theme'}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {themes.map((theme) => {
            const isPending = pendingThemeId === theme.id;
            const isApplied = appliedThemeId === theme.id;
            const isDraft = theme.status === 'draft';
            return (
              <button
                key={theme.id}
                type="button"
                disabled={saving}
                onClick={() => handlePick(theme.id)}
                className={`text-left p-4 rounded-2xl border-2 transition-all ${
                  isPending
                    ? 'border-[#0c0f14] bg-[#eaf3ef] shadow-sm'
                    : 'border-[#e8ece9] bg-white hover:border-[#dcebe4]'
                } ${isDraft ? 'opacity-90' : ''}`}
              >
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div>
                    <p className="font-semibold text-[#0c0f14] text-sm">{theme.name}</p>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {isApplied && (
                        <span className="text-[0.65rem] uppercase tracking-wide font-bold text-[#0c0f14] bg-[#dcebe4] px-1.5 py-0.5 rounded">
                          Active
                        </span>
                      )}
                      {isPending && hasChanges && (
                        <span className="text-[0.65rem] uppercase tracking-wide font-bold text-[#5c6670] bg-[#f6f7f5] px-1.5 py-0.5 rounded">
                          Preview
                        </span>
                      )}
                      {isDraft && (
                        <span className="text-[0.65rem] uppercase tracking-wide font-bold text-[#5c6670]">
                          Draft slot
                        </span>
                      )}
                    </div>
                  </div>
                  {isPending && (
                    <span className="w-6 h-6 rounded-full bg-[#0c0f14] text-white flex items-center justify-center shrink-0">
                      <Check className="w-3.5 h-3.5" strokeWidth={3} />
                    </span>
                  )}
                </div>
                <div className="flex gap-1.5 mb-2">
                  {theme.swatches.map((color) => (
                    <span
                      key={color}
                      className="w-7 h-7 rounded-lg border border-black/5"
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
                <p className="text-xs text-[#5c6670] leading-relaxed">{theme.description}</p>
              </button>
            );
          })}
        </div>

        <div className="mt-6 p-4 rounded-xl bg-[#f6f7f5] border border-[#e8ece9] text-xs text-[#5c6670] space-y-2">
          <p className="font-semibold text-[#0c0f14] text-sm">What happens when you apply?</p>
          <ul className="list-disc pl-4 space-y-1">
            <li>Saves <code className="bg-white px-1 rounded">defaultThemeId</code> to Firebase (platform/settings).</li>
            <li>Updates colors on your admin screen immediately (buttons, sidebar, home page, user app styles).</li>
            <li>All users <strong>without</strong> a personal theme in Settings see this theme on next page load.</li>
            <li>Users who picked their own theme in Settings keep their choice until they reset to platform default.</li>
            <li>Theme tokens are defined in <code className="bg-white px-1 rounded">src/styles/themes.css</code>.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminThemesPanel;
